import React, { useEffect, useState } from 'react';
import { Empty, Skeleton } from 'antd';
import Chart from '@components/charts/Chart';
import { apiGetConsumptionComparisonByBranch } from '@services/ManageDevices/DeviceServices';
import { showError } from '@components/notification';
import { formatCurrency } from '@utils/utils';
import { barOptions } from '../dashboardTheme';

// Oldest → newest period: gray, orange, indigo (the dashboard's original colors).
const PERIOD_COLORS = ['#9E9E9E', '#FF9800', '#4f46e5d9'];

const formatPeriodLabel = (periodStr) => {
  if (!periodStr) return '';
  const date = new Date(periodStr);
  return `Kỳ ${date.getMonth() + 1}/${date.getFullYear()}`;
};

const ConsumptionComparison = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [categories, setCategories] = useState([]);

  const fetchData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await apiGetConsumptionComparisonByBranch();
      if (res.data?.data?.zones && Array.isArray(res.data.data.zones)) {
        const branchesData = res.data.data.zones;
        const periods = res.data.data.periods || [];

        setCategories(branchesData.map((branch) => branch.zone_name || branch.zone_code));

        // Chỉ lấy 3 kỳ gần nhất và đảo ngược để hiển thị từ cũ đến mới
        const recentPeriods = periods.slice(-3).reverse();
        const colors = PERIOD_COLORS.slice(-recentPeriods.length);

        setData(
          recentPeriods.map((period, periodIndex) => {
            const originalIndex = periods.findIndex((p) => p === period);
            return {
              name: formatPeriodLabel(period),
              data: branchesData.map((branch) =>
                parseFloat(branch.consumptions?.[originalIndex]?.consumption || 0)
              ),
              color: colors[periodIndex]
            };
          })
        );
      }
    } catch (error) {
      console.error('Error fetching consumption comparison:', error);
      showError('Không thể tải dữ liệu so sánh');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Auto refresh every 5 minutes
    const interval = setInterval(() => fetchData(true), 300000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return <Skeleton active paragraph={{ rows: 8 }} />;
  }

  if (data.length === 0) {
    return <Empty className="chart-empty" description="Chưa có dữ liệu" />;
  }

  return (
    <div className="chart-container">
      <Chart
        options={barOptions({
          colors: data.map((s) => s.color),
          plotOptions: { bar: { columnWidth: '64%', borderRadius: 4 } },
          stroke: { width: 2 },
          xaxis: { categories },
          yaxis: { labels: { formatter: (val) => formatCurrency(val, 0) } },
          tooltip: {
            shared: true,
            intersect: false,
            y: { formatter: (val) => `${formatCurrency(val, 2)} m³` }
          },
          legend: { position: 'top', horizontalAlign: 'right' }
        })}
        type="bar"
        series={data}
        height={340}
      />
    </div>
  );
};

export default ConsumptionComparison;
